/*
 * Hero / band figures arrive as free-form strings: "87+", "$12.4B", "$ 1.2B".
 * Split each value into prefix + numeric core + suffix. Symbols stay put;
 * only the digits in the number roll, like a slot machine that lands on 12.4
 * while "$" and "B" never move.
 */

const SELECTOR = '.animateUpNumbers'
const REEL_CYCLES = 2
const BASE_DURATION = 1.9
const DIGIT_STAGGER = 0.18

function parseFigure(text) {
	const raw = String(text).trim()
	const first = raw.search(/\d/)
	if (first === -1) return null

	let last = first
	for (let i = raw.length - 1; i >= first; i -= 1) {
		if (/\d/.test(raw[i])) {
			last = i
			break
		}
	}

	const prefix = raw.slice(0, first)
	const suffix = raw.slice(last + 1)
	const core = raw.slice(first, last + 1)
	const compact = core.replace(/[^\d.,]/g, '')
	const commaThousands = /^\d{1,3}(,\d{3})+(\.\d+)?$/.test(compact)
	const numeric = compact.replace(/,/g, '')
	const value = parseFloat(numeric)

	if (!Number.isFinite(value)) return null

	const dot = numeric.lastIndexOf('.')
	const decimals = dot === -1 ? 0 : numeric.length - dot - 1

	return { prefix, suffix, value, decimals, commaThousands }
}

function formatNumber(value, decimals, commaThousands) {
	const fixed = value.toFixed(decimals)
	if (!commaThousands) return fixed

	const [intPart, frac] = fixed.split('.')
	const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
	return decimals ? `${grouped}.${frac}` : grouped
}

function appendAffix(parent, className, text) {
	if (!text) return
	const el = document.createElement('span')
	el.className = className
	el.textContent = text
	parent.appendChild(el)
}

function buildReel(digit, index) {
	const reel = document.createElement('span')
	reel.className = 'figure__reel'

	const target = Number(digit)
	const values = [0]

	for (let cycle = 0; cycle < REEL_CYCLES; cycle += 1) {
		for (let n = 1; n <= 9; n += 1) values.push(n)
		values.push(0)
	}

	if (target > 0) {
		for (let n = 1; n <= target; n += 1) values.push(n)
	}

	values.forEach((value) => {
		const cell = document.createElement('span')
		cell.textContent = String(value)
		reel.appendChild(cell)
	})

	reel.style.setProperty('--reel-duration', `${BASE_DURATION + index * DIGIT_STAGGER}s`)
	reel.style.setProperty('--reel-delay', `${index * 90}ms`)

	return { reel, travel: values.length - 1 }
}

function buildFigure(parsed) {
	const finalNumber = formatNumber(parsed.value, parsed.decimals, parsed.commaThousands)

	const figure = document.createElement('span')
	figure.className = 'figure'
	figure.setAttribute('aria-hidden', 'true')

	appendAffix(figure, 'figure__prefix', parsed.prefix)

	const number = document.createElement('span')
	number.className = 'figure__number'

	const reels = []
	let digitIndex = 0

	Array.from(finalNumber).forEach((char) => {
		if (/\d/.test(char)) {
			const digit = document.createElement('span')
			digit.className = 'figure__digit'
			const { reel, travel } = buildReel(char, digitIndex)
			digit.appendChild(reel)
			number.appendChild(digit)
			reels.push({ reel, travel })
			digitIndex += 1
		} else {
			const sep = document.createElement('span')
			sep.className = 'figure__sep'
			sep.textContent = char
			number.appendChild(sep)
		}
	})

	figure.appendChild(number)
	appendAffix(figure, 'figure__suffix', parsed.suffix)

	return { figure, reels, finalNumber }
}

function motionWait(el) {
	const group = el.closest('[data-motion]')
	if (!group) return 0

	const groupDelay = parseFloat(getComputedStyle(group).getPropertyValue('--motion-delay')) || 0
	const item = el.closest('[data-motion] > *')
	const stagger = item
		? parseFloat(getComputedStyle(item).getPropertyValue('--motion-stagger')) || 0
		: 0

	return groupDelay + stagger
}

function spin(reels) {
	requestAnimationFrame(() => {
		reels.forEach(({ reel, travel }) => {
			reel.style.setProperty('--reel-travel', travel)
		})
	})
}

function roll(el) {
	const text = el.textContent.trim()
	const parsed = parseFigure(text)
	if (!parsed) return

	const { figure, reels, finalNumber } = buildFigure(parsed)

	const label = document.createElement('span')
	label.className = 'vh'
	label.textContent = `${parsed.prefix}${finalNumber}${parsed.suffix}`

	el.textContent = ''
	el.appendChild(label)
	el.appendChild(figure)

	const wait = motionWait(el)

	window.setTimeout(() => spin(reels), wait)
}

export default function initCounters() {
	const elements = Array.from(document.querySelectorAll(SELECTOR))
	if (!elements.length) return

	if (
		window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
		!('IntersectionObserver' in window)
	) {
		return
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return
				roll(entry.target)
				observer.unobserve(entry.target)
			})
		},
		{ threshold: 0.25 }
	)

	const heroEls = []

	elements.forEach((el) => {
		if (el.closest('.section--hero')) {
			heroEls.push(el)
			return
		}
		observer.observe(el)
	})

	if (!heroEls.length) return

	const startHero = () => heroEls.forEach(roll)

	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(startHero).catch(startHero)
	} else {
		startHero()
	}
}
